import {
  observable, makeObservable,
} from 'mobx';
import { Axios } from '../utilities/network';
import RootStore from './root-store';
import { CURRENT_PLATFORM } from '../constants/settings';
import { FAxios } from "../utilities/file-network";



export default class CanvasStore {
  loading: boolean = false;
  loadingInfo: boolean = false;
  creating: boolean = false;
  updating: boolean = false;
  deleting: boolean = false;
  uploading: boolean = false;
  loadingFields: boolean = false;
  downloading: boolean = false;

  rootStore: RootStore;

  constructor(rootStore: RootStore) {
    makeObservable(this, {
      loading: observable,
      loadingInfo: observable,
      creating: observable,
      updating: observable,
      deleting: observable,
      uploading: observable,
      loadingFields: observable,
      downloading: observable,
    });
    this.rootStore = rootStore;
  }

  searchCanvases = async (skip: number = 0, take: number = 0, reqModel: any = {}) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Canvas/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  };

  createCanvas = async (reqModel: any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Canvas?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  updateCanvas = async (reqModel: any) => {
    this.updating = true;
    try {
      const res = await Axios.put(`/api/Canvas?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updating = false;
    }
  }

  deleteCanvas = async (id: number) => {
    this.deleting = true;
    try {
      const res = await Axios.delete(`/api/Canvas/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deleting = false;
    }
  }

  uploadCanvasImage = async (reqModel: any) => {
    this.uploading = true;
    try {
      const res = await Axios.post(`/api/Canvas/Image?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploading = false;
    }
  }

  deleteCanvasImage = async (id: number) => {
    this.deleting = true;
    try {
      const res = await Axios.delete(`/api/Canvas/Image/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deleting = false;
    }
  }

  searchCanvasImages = async (id: number, skip: number = 0, take: number = 0) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Canvas/Image/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, { templateId: id });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  createImagePoint = async (reqModel: any) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Canvas/Image/Point?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  updateImagePoint = async (reqModel: any) => {
    this.updating = true;
    try {
      const res = await Axios.put(`/api/Canvas/Image/Point?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updating = false;
    }
  }

  deleteImagePoint = async (id: number) => {
    this.deleting = true;
    try {
      const res = await Axios.post(`/api/Canvas/Image/Point/Delete?platformId=${CURRENT_PLATFORM}`, [id]);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deleting = false;
    }
  }

  searchImagePoints = async (imageId: number, skip: number = 0, take: number = 0) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Canvas/Image/Point/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, { imageId });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  // Clinic Template
  searchDocumentTemplate = async (skip: number = 0, take: number = 0, reqModel: any = {}) => {
    this.loading = true;
    try {
      const res = await Axios.post(`/api/Document/Template/Search/${skip}/${take}?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loading = false;
    }
  }

  createTemplate = async (reqModel: any) => {
    this.creating = true;
    try {
      const res = await Axios.post(`/api/Document/Template?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.creating = false;
    }
  }

  updateDocumentTemplate = async (reqModel: any) => {
    this.updating = true;
    try {
      const res = await Axios.put(`/api/Document/Template?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updating = false;
    }
  }

  deleteTemplate = async (id: number) => {
    this.deleting = true;
    try {
      const res = await Axios.delete(`/api/Document/Template/${id}?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.deleting = false;
    }
  }

  getTemplateById = async (id: number, clinicId: number) => {
    this.loadingInfo = true;
    try {
      const res = await Axios.get(`/api/Document/Template/${id}?clinicId=${clinicId}&platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingInfo = false;
    }
  }

  searchTemplateCategories = async() => {
    this.uploading = true;
    try {
      const res = await Axios.get(`/api/Document/Category/Search?platformId=${CURRENT_PLATFORM}`);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.uploading = false;
    }
  }

  searchTemplateField = async (reqModel: any = {}) => {
    this.loadingFields = true;
    try {
      const res = await Axios.post(`/api/Clinic/Document/Template/Field/Search?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.loadingFields = false;
    }
  }
  
  toggleActiveTemplate = async (isActive: boolean, reqModel: any) => {
    this.updating = true;
    try {
      let url = `/api/Clinic/Document/Template/`;
      if (isActive) url += `Active`;
      else url += `Inactive`;
      const res = await Axios.post(`${url}?platformId=${CURRENT_PLATFORM}`, reqModel);
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.updating = false;
    }
  }

  downloadDocumentByTemplate = async (reqModel: any, isPdf: boolean = true) => {
    this.downloading = true;
    try {
      let url = `/api/Clinic/Document/Template`;
      if (isPdf) url += `/Pdf`;
      else url += `/Word`;
      const res = await FAxios.post(`${url}?platformId=${CURRENT_PLATFORM}`, reqModel, { responseType: 'blob' });
      return Promise.resolve(res.data);
    } catch (err) {
      return Promise.reject(err);
    } finally {
      this.downloading = false;
    }
  }

}
